import React, { useMemo, useState } from 'react'
import { Trash } from 'lucide-react'

import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'shadcdn/ui/table'
import { Badge } from 'ui/Badge/Badge'
import { Checkbox } from 'shadcdn/ui/checkbox'
import { Button } from 'ui/Button/Button'
import { cn } from 'utils/cn'
// prettier-ignore
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from 'shadcdn/ui/alert-dialog'
import { useInstalledComponents } from 'features/app/hooks'
import type { InstalledComponent } from 'features/versions/types'
import { Input } from 'shadcdn/ui/input'
import { appAPI, isApp } from 'utils/app-api'

type Props = {}

export const InstalledSoftwarePage = (_props: Props) => {
  const { groupedPlugins, plugins, refetch } = useInstalledSoftwarePageData()
  const [checkedPlugins, setCheckedPlugins] = useState<Record<string, boolean>>({})
  const [deletedPlugins, setDeletedPlugins] = useState<string[]>([])
  const [filter, setFilter] = useState<string>('')

  const checkedPluginsCount = useMemo(() => Object.values(checkedPlugins).filter(Boolean).length, [checkedPlugins])

  const filteredPlugins = useMemo(() => {
    if (!filter) return plugins
    return plugins.filter((name) => name.toLowerCase().includes(filter.toLowerCase()))
  }, [filter, plugins])

  const handleDelete = async () => {
    if (!isApp) return
    const pluginNamesToDelete: string[] = Object.entries(checkedPlugins)
      .filter(([, checked]) => checked)
      .map(([name]) => name)

    const pluginsToDelete: string[] = pluginNamesToDelete
      .flatMap((name) => groupedPlugins[name])
      .map((comp) => comp.filename)
    if (!pluginsToDelete.length) return

    const deleted = await appAPI.deleteComponents(pluginsToDelete)
    setCheckedPlugins({})
    setDeletedPlugins(deleted)

    await refetch()
  }

  if (!plugins && !isApp) return <AppRequiredError />
  if (!plugins) return null

  return (
    <div className="relative">
      <div className="p-3 pl-2 pr-0 sticky bg-white top-9 z-10 border-b flex gap-3">
        <h1 className="text-xl font-light flex-grow">Installed Software</h1>
        <div>
          {!!checkedPluginsCount && (
            <Button color="light" size="sm" className="-mt-2" onClick={handleDelete}>
              <Trash className="inline-block w-3 h-3 -mt-0.5 mr-1" />
              Delete {checkedPluginsCount} Plugin(s)
            </Button>
          )}
        </div>
        <div>
          <Input
            className="px-1.5 py-0.5 text-sm text-gray-500"
            placeholder="Filter"
            onChange={(e) => setFilter(e.target.value)}
          />
        </div>
      </div>

      <DeletedSoftwareModal deletedPlugins={deletedPlugins} onOkClick={() => setDeletedPlugins([])} />
      <Table>
        <TableHeader>
          <TableRow className="*:p-2 *:h-auto text-xs">
            <TableHead />
            <TableHead>Plugin</TableHead>
            <TableHead>Version</TableHead>
            <TableHead>Formats</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filteredPlugins.map((name) => {
            const version = groupedPlugins[name][0].version
            const extensions = groupedPlugins[name].map((comp) => comp.extension)
            const checked: boolean = checkedPlugins[name]
            const toggleCheckbox = () => setCheckedPlugins({ ...checkedPlugins, [name]: !checked })
            return (
              <TableRow
                key={name}
                className={cn('cursor-default hover:bg-gray-ingerit', { 'bg-slate-200': checked })}
                onClick={toggleCheckbox}
              >
                <TableCell className="p-2 pt-2.5 pb-1.5 w-4">
                  <Checkbox onChange={toggleCheckbox} checked={checked} />
                </TableCell>
                <TableCell className="p-2">
                  <HighlightTextParts text={name} highlightedText={filter} />
                </TableCell>
                <TableCell className="p-2">{version}</TableCell>
                <TableCell className="p-2">
                  {extensions.map((ext) => (
                    <Badge key={ext} label={ext} color="gray" className="text-[0.6rem]" />
                  ))}
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </div>
  )
}

const HighlightTextParts = React.memo((props: { text: string; highlightedText: string }) => {
  const { text, highlightedText } = props
  if (!highlightedText) return <>{text}</>

  const regex = new RegExp(`(${highlightedText})`, 'ig')
  const parts = text.split(regex)

  return (
    <>
      {parts.map((part, i) => (
        <span key={i}>
          {part.toLowerCase() === highlightedText.toLowerCase() ? <span className="bg-yellow-200">{part}</span> : part}
        </span>
      ))}
    </>
  )
})

const useInstalledSoftwarePageData = (_useApps = false) => {
  const { installedComponents, refetch } = useInstalledComponents()

  const groupedPlugins: Record<string, InstalledComponent[]> = useMemo(() => {
    const _groupedPlugins: any =
      installedComponents?.reduce(
        (acc, installedComponent) => {
          const filename = installedComponent.componentPath.split('/').pop()
          if (!filename) throw new Error('filename should be defined')
          const [name] = filename.split('.')
          acc[name] ??= []
          acc[name].push(installedComponent)
          return acc
        },
        {} as Record<string, InstalledComponent[]>,
      ) ?? {}
    for (const name in _groupedPlugins) {
      const components: InstalledComponent[] = _groupedPlugins[name]
      // don't show apps with only .app extension
      if (components.length === 1 && components[0].extension === '.app') {
        delete _groupedPlugins[name]
      }
    }
    return _groupedPlugins
  }, [installedComponents])

  const plugins = useMemo(() => Object.keys(groupedPlugins).toSorted(), [groupedPlugins])
  return {
    groupedPlugins,
    plugins,
    refetch,
  }
}
const DeletedSoftwareModal = (props: { deletedPlugins: string[]; onOkClick: () => unknown }) => {
  return (
    <AlertDialog open={!!props.deletedPlugins?.length}>
      <AlertDialogContent className="max-w-3xl">
        <AlertDialogHeader>
          <AlertDialogTitle>Plugins uninstalled</AlertDialogTitle>
          <AlertDialogDescription>
            <div className="p-2 ">
              <span className="font-semibold text-sm">Deleted files</span>
              <pre className="p-4 text-xs bg-gray-100 border border-gray-200">{props.deletedPlugins.join('\n')}</pre>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction onClick={props.onOkClick}>OK</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

const AppRequiredError = () => (
  <div className="text-center pt-20">
    <h3 className="text-2xl mb-4">:(</h3>
    <div className="text-sm">
      Installed software management in App only. Please install the app from{' '}
      <a className="link" href="https://presync.io">
        presync.io
      </a>
    </div>
  </div>
)
